'use client';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';
import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled from 'styled-components';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const CalendarContainer = styled.div`
  .react-calendar {
    background: #ff3339ff;
  }

  /* Month and arrows section */
  .react-calendar__navigation button {
    background: #3344ffff;
    color: white; /* White text color */
  }

  /* Month names section */
  .react-calendar__month-view__weekdays,
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    color: white; /* White text color */
    background: #ff3339ff; /* Red background */
  }

  .react-calendar__tile--active {
    background-color: white !important;
    color: #3344ffff !important;
  }
  .react-calendar__month-view__weekdays {
    color: white;
  }

  .react-calendar__month-view__days__day--today {
    background: orange;
    color: white;
  }

  .react-calendar__tile--now {
    background: #ff8286;
    color: #3344ffff;

    &:hover {
      background: #ff8286 !important;
      color: white !important;
    }
  }

  .react-calendar__month-view__days button {
    position: relative;
    color: white;

    &:hover {
      background: #8690ff;
    }
  }

  .react-calendar__month-view__days__day--weekend {
    color: #d4d7ff !important;

    &:hover {
      color: white;
    }
  }

  .react-calendar__month-view__days__day--weekend--active {
    &:hover {
      color: #ff3339ff !important;
    }
  }
  .react-calendar__tile--active {
    background-color: white;
    color: #ff3339ff !important;
  }

  .react-calendar__navigation button {
    &:hover {
      background-color: #ff8286;
    }
  }
`;

// C5E7EF
//red #ff3339ff
//blue #3344ffff

type Event = {
  name: string;
  description?: string;
  city?: string;
};

type Events = { [date: string]: Event };

interface CalendarEvents {
  calendarEvents: Reference[];
}

const MyCalendar = ({ calendarEvents }: CalendarEvents) => {
  const [events, setEvents] = useState<Events>({});
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isMonthClicked, setIsMonthClicked] = useState<boolean>(false);

  useEffect(() => {
    const newEvents: Events = {};
    for (let i = 0; i < calendarEvents.length; i++) {
      const event = calendarEvents[i];
      if (event.firstname && event.firstname !== '' && event.firstname.includes('.')) {
        const dateParts = event.firstname.split('.');
        const year = dateParts[2];
        const month = dateParts[1].padStart(2, '0');
        const day = dateParts[0].padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        newEvents[formattedDate] = {
          name: event.name,
          description: event.description,
          city: event.city,
        };
      }
    }
    setEvents(newEvents);
  }, [calendarEvents]);

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setIsMonthClicked(false);
  };

  const handleMonthClick = (date: Date) => {
    setSelectedDate(date);
    setIsMonthClicked(true);
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const dateString = formatDate(date);
      const dateEvents = events[dateString];
      if (dateEvents) {
        return <div className="absolute bottom-1 left-1 size-[8px] rounded-full bg-[#d4d7ff]" />;
      }
    }
    return null;
  };

  function formatDate(date: Date): string {
    const tzOffset = date.getTimezoneOffset();
    const tzOffsetMilliseconds = tzOffset * 60 * 1000;
    const localDate = new Date(date.getTime() - tzOffsetMilliseconds);
    return localDate.toISOString().split('T')[0];
  }

  const options = {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as Intl.DateTimeFormatOptions;

  const renderDate = (date: Date): string => {
    return date.toLocaleDateString('sk', options).replace(/^\w/, c => c.toUpperCase());
  };

  const renderEvents = () => {
    if (!selectedDate) return null;

    const dateString = formatDate(selectedDate);
    const event = events[dateString];
    if (!event) {
      return <h2>Žiadne nadchádzajúce podujatia na tento deň.</h2>;
    }

    return (
      <div className="w-full">
        <div className="text-xl font-bold md:text-3xl">{event.name}</div>
        <h2>Miesto konania: {event.city}</h2>
        {event.description && <HtmlRendererRaw html={event.description} />}
      </div>
    );
  };

  // Filter events for the selected month
  const monthEvents = Object.entries(events)
    .filter(([dateString]) => {
      const eventDate = new Date(dateString);
      return eventDate.getMonth() === selectedDate?.getMonth();
    })
    .map(([dateString, event]) => {
      const eventDate = new Date(dateString);
      return (
        <div className="w-full lg:max-w-[50%]" key={dateString}>
          <h2>{renderDate(eventDate)}</h2>
          <p className="text-xl font-bold md:text-3xl">{event.name}</p>
          <h2>Miesto konania: {event.city}</h2>
          {event.description && <HtmlRendererRaw as="p" html={event.description} />}
        </div>
      );
    });

  // Render message if there are no events for the selected month
  const renderMonthEvents = () => {
    if (!selectedDate) return null;
    if (monthEvents.length === 0) {
      return <h2>Žiadne nadchádzajúce podujatia na tento mesiac.</h2>;
    } else {
      return monthEvents;
    }
  };

  return (
    <div className="mx-auto flex w-full flex-col items-center justify-center gap-10 py-10 md:items-start md:justify-start lg:flex-row">
      <div className="">
        <CalendarContainer>
          <Calendar
            onClickDay={handleDateClick}
            onClickMonth={handleMonthClick}
            tileContent={tileContent}
          />
        </CalendarContainer>
      </div>
      <div className="flex w-full flex-col">
        {selectedDate && !isMonthClicked ? (
          <>
            <h2>{selectedDate.toLocaleDateString('sk', options)}</h2>
            {renderEvents()}
          </>
        ) : (
          <div className="flex w-full flex-col gap-10 md:flex-row">{renderMonthEvents()}</div>
        )}
      </div>
    </div>
  );
};

export { MyCalendar };

//---------------------------------------------------------------------
